import { useRef, useEffect } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-console/box";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";

import { getString } from "../../common/ui-string-labels/ui-strings-utils";
import { useChatbot, Sender } from "../../context/chatbot/ChatbotContext";
import chatbotConfig from "./chatbot-config.json";
import LoadingMessage from "./ChatbotMessages/LoadingMessage";
import BotMessage from "./ChatbotMessages/BotMessage";
import UserMessage from "./ChatbotMessages/UserMessage";
import ChatInput from "./ChatbotMessages/ChatInput";
import QuickActions from "./QuickActions/QuickActions";

import './ChatbotContent.scss';

interface ChatbotContentProps {
    variant?: "widget" | "page";
}

const ChatbotContent = ({ variant = "widget" }: ChatbotContentProps) => {
    const { messages, isLoading, transcriptLoading, sendMessage, quickActionsVisible } = useChatbot();
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container) {
            const rafId = requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight;
            });
            return () => cancelAnimationFrame(rafId);
        }
    }, [messages]);

    return (
        <div className={`chatbot-layout chatbot-layout--${variant}`} data-testid={`chatbot-content-${variant}`}>
            <div className="chatbot-messages" ref={messagesContainerRef}>
                {transcriptLoading ? (
                    <div className="chatbot-messages__loading" role="status" data-testid="chatbot-transcript-loading">
                        <Spinner size="large" />
                        <Box color="text-status-inactive">{getString('chatbot.messages.loadingConversation')}</Box>
                    </div>
                ) : (
                <Box margin="xs" padding="xs">
                    <SpaceBetween size="m">
                        {messages.map((message) => {
                            if (message.loading) return <LoadingMessage key={message.id} id={message.id} />;
                            return message.sender === Sender.BOT ? (
                                <BotMessage
                                    key={message.id}
                                    message={message}
                                />
                            ) : (
                                <UserMessage key={message.id} message={message} />
                            );
                        })}
                        {quickActionsVisible && !isLoading && <QuickActions onSend={sendMessage} />}
                    </SpaceBetween>
                </Box>
                )}
            </div>
            <div className="chatbot-footer">
                <ChatInput
                    onSend={sendMessage}
                    isLoading={isLoading}
                    maxChars={chatbotConfig.maxChars}
                />
            </div>
        </div>
    );
};

export default ChatbotContent;
